import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DetailsTicket from './DetailsTicket';
import ActivityTicket from './ActivityTicket';
import CreateTicketDrawer from '../CreateTicketDrawer';
import { ticketApis } from '../../../Data/axios';
import { loadTicketPermissions, hasTicketPermission } from '../../../utils/ticketPermissions';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { API_CONFIG, ERROR_EXPIRY_MS } from './utils/config';
import { getStoredUploadError, clearStoredUploadError } from './utils/fileHandler';
import EditIcon from '@mui/icons-material/Edit';

const ViewMoreTickets = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const rowData = location.state || {}; // Fallback to empty object

  const [activeTab, setActiveTab] = useState("details");
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [canEditTicket, setCanEditTicket] = useState(false);

  const hasMounted = useRef(false);
  const successTimerRef = useRef(null);
  const errorTimerRef = useRef(null);

  // Check for stored upload errors on mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;

      const storedError = getStoredUploadError(ERROR_EXPIRY_MS);
      if (storedError && storedError.message) {
        setUploadError({
          message: storedError.message,
          timestamp: storedError.timestamp || Date.now()
        });
        clearStoredUploadError();
      }
    }
  }, []);

  // Auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSuccessMessage(null), 5000);
    }
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, [successMessage]);

  // Auto-dismiss error message after 5 seconds
  useEffect(() => {
    if (uploadError) {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => setUploadError(null), 5000);
    }
    return () => {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    };
  }, [uploadError]);

  // Fetch ticket data on component mount
  const fetchTicketData = useCallback(async () => {
    setLoading(true);
    setError(null);

    console.log('Fetching ticket details for ID:', id);

    try {
      const response = await ticketApis.getTicketById(id);

      console.log('Ticket API response:', response);
      console.log('Response success:', response.success);
      console.log('Response data:', response.data);

      if (response.success && response.data) {
        console.log('Setting ticket data:', response.data);
        setTicket(response.data);
      } else if (!response.success) {
        console.error('API returned unsuccessful:', response);
        setError(response.message || 'Failed to load ticket details');
      } else {
        console.error('No data in response:', response);
        setError('No ticket data available');
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      console.error('Error details:', err.message);
      console.error('Error stack:', err.stack);
      setError('An error occurred while loading ticket details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTicketData();
  }, [fetchTicketData]);

  // Handle tab switching
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle navigation back
  const handleBack = () => {
    navigate(-1);
  };

  // Clear upload error
  const handleClearError = () => {
    setUploadError(null);
  };

  // Clear success message
  const handleClearSuccess = () => {
    setSuccessMessage(null);
  };

  // Show success alert
  const handleShowSuccessAlert = (message) => {
    setSuccessMessage({
      message: typeof message === 'string' ? message : (message?.message || 'Success'),
      timestamp: Date.now()
    });
  };

  // Validate upload error structure
  const getUploadErrorMessage = () => {
    if (!uploadError) return null;
    if (typeof uploadError === 'string') return uploadError;
    return uploadError.message || 'An error occurred';
  };

  // Safe error alert function
  const handleShowErrorAlert = (errorData) => {
    let errorMessage;

    if (typeof errorData === 'string') {
      errorMessage = errorData;
    } else if (errorData && errorData.message) {
      errorMessage = errorData.message;
    } else {
      errorMessage = 'An unknown error occurred';
    }

    setUploadError({
      message: errorMessage,
      timestamp: Date.now()
    });
  };

  useEffect(() => {
    loadTicketPermissions().then((perms) => {
      setCanEditTicket(hasTicketPermission(perms, "ticket_edit"));
    });
  }, []);

  // Handle edit button click
  const handleEditTicket = () => {
    if (!canEditTicket) {
      showSnackbar("No permission to edit", "error");
      return;
    }
    if (ticket) {
      setEditDrawerOpen(true);
    }
  };

  // Handle ticket update
  const handleTicketUpdated = () => {
    // Refresh ticket data after update
    fetchTicketData();
    setEditDrawerOpen(false);
  };

  // Handle ticket update failure
  const handleEditAlert = (message, severity) => {
    if (severity === 'success') {
      handleShowSuccessAlert(message);
    } else {
      handleShowErrorAlert(message);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="w-full bg-gray-50 min-h-screen font-ubuntu flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="w-full bg-gray-50 min-h-screen font-ubuntu flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Ticket</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="bg-gray-800 text-white px-6 py-2 rounded-md hover:bg-gray-900 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 min-h-screen font-ubuntu">
      <div className="w-full">
        {/* Header Block */}
        <div className="bg-white shadow-sm border-b border-gray-100 p-3 mb-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div>
            <div
              className="text-gray-400 text-xs font-medium mb-1 flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors"
              onClick={handleBack}
            >
              <span>Ticket #</span>
              <span className="text-gray-800">{ticket?.ticket_id || ticket?.id || rowData.id || id}</span>
            </div>
            <h1 className="text-gray-900 text-xl font-bold">{ticket?.subject || rowData.subject || 'Ticket Subject'}</h1>
          </div>
          <div className="flex border border-gray-200 rounded-md overflow-hidden bg-white">
            <button
              onClick={() => handleTabChange("details")}
              className={`px-6 py-1.5 text-sm font-medium transition-colors ${
                activeTab === "details"
                  ? "bg-orange-500 text-white"
                  : "bg-white text-gray-500 hover:text-gray-800"
              }`}
            >
              Details
            </button>
            <button
              onClick={handleEditTicket}
              className={`px-6 py-1.5 text-sm font-medium transition-colors flex items-center gap-1 border-l border-r border-gray-100 ${
                !canEditTicket || !ticket
                  ? "bg-white text-gray-300 cursor-not-allowed opacity-60"
                  : "bg-white text-gray-500 hover:text-gray-800 hover:bg-gray-50"
              }`}
              disabled={!ticket}
            >
              <EditIcon fontSize="small" />
              Edit
            </button>
            <button
              onClick={() => handleTabChange("activity")}
              className={`px-6 py-1.5 text-sm font-medium transition-colors ${
                activeTab === "activity"
                  ? "bg-orange-500 text-white"
                  : "bg-white text-gray-500 hover:text-gray-800"
              }`}
            >
              Activity
            </button>
          </div>
        </div>

        {/* Success Alert */}
        {successMessage && (
          <div className="mx-4 mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <div className="text-green-500 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-green-700 mb-2">{successMessage.message}</p>
              <button
                onClick={handleClearSuccess}
                className="text-sm text-green-600 hover:text-green-800 font-medium underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Upload Error Alert */}
        {uploadError && (
          <div className="mx-4 mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <div className="text-red-500 flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-red-800 text-sm mb-1">Upload Error</h4>
              <p className="text-sm text-red-700 mb-2">{getUploadErrorMessage()}</p>
              <button
                onClick={handleClearError}
                className="text-sm text-red-600 hover:text-red-800 font-medium underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="w-full">
          {activeTab === "details" ? (
            <DetailsTicket ticket={ticket} baseUrl={API_CONFIG.apiDBUploadsUrl} />
          ) : (
            <ActivityTicket
              ticketId={id}
              baseUrl={API_CONFIG.apiBaseUrl}
              uploadBaseUrl={API_CONFIG.apiDBUploadsUrl}
              showErrorAlert={handleShowErrorAlert}
              showSuccessAlert={handleShowSuccessAlert}
            />
          )}
        </div>
      </div>

      {/* CreateTicketDrawer for editing */}
      <CreateTicketDrawer
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        onTicketUpdated={handleTicketUpdated}
        showAlert={handleEditAlert}
        mode="update"
        ticketData={ticket}
      />
    </div>
  );
};

export default ViewMoreTickets;
